import type { Transition } from '@tbcast/shared';

/**
 * GPU-friendly transitions using only transform and opacity. The incoming
 * element starts in its "from" state, the outgoing one moves to its "to" state.
 */
interface Frames {
  inFrom: Partial<CSSStyleDeclaration>;
  outTo: Partial<CSSStyleDeclaration>;
}

const FRAMES: Record<Transition, Frames> = {
  none: { inFrom: {}, outTo: {} },
  fade: { inFrom: { opacity: '0' }, outTo: { opacity: '0' } },
  dissolve: {
    inFrom: { opacity: '0', filter: 'blur(6px)' },
    outTo: { opacity: '0', filter: 'blur(6px)' },
  },
  'slide-left': {
    inFrom: { transform: 'translate3d(100%,0,0)' },
    outTo: { transform: 'translate3d(-30%,0,0)', opacity: '0.4' },
  },
  'slide-right': {
    inFrom: { transform: 'translate3d(-100%,0,0)' },
    outTo: { transform: 'translate3d(30%,0,0)', opacity: '0.4' },
  },
  'slide-up': {
    inFrom: { transform: 'translate3d(0,100%,0)' },
    outTo: { transform: 'translate3d(0,-30%,0)', opacity: '0.4' },
  },
  'slide-down': {
    inFrom: { transform: 'translate3d(0,-100%,0)' },
    outTo: { transform: 'translate3d(0,30%,0)', opacity: '0.4' },
  },
  push: {
    inFrom: { transform: 'translate3d(100%,0,0)' },
    outTo: { transform: 'translate3d(-100%,0,0)' },
  },
  zoom: {
    inFrom: { transform: 'scale(1.15)', opacity: '0' },
    outTo: { transform: 'scale(0.92)', opacity: '0' },
  },
  flip: {
    inFrom: { transform: 'perspective(1600px) rotateY(-90deg)', opacity: '0' },
    outTo: { transform: 'perspective(1600px) rotateY(90deg)', opacity: '0' },
  },
};

const reducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

function apply(el: HTMLElement, styles: Partial<CSSStyleDeclaration>) {
  for (const [k, v] of Object.entries(styles))
    (el.style as unknown as Record<string, string>)[k] = v as string;
}

function clear(el: HTMLElement) {
  el.style.transition = '';
  el.style.transform = '';
  el.style.opacity = '';
  el.style.filter = '';
}

/** Runs a transition between two layers; resolves when finished. */
export async function runTransition(
  incoming: HTMLElement,
  outgoing: HTMLElement | null,
  kind: Transition,
  durationMs: number,
): Promise<void> {
  const t = reducedMotion() ? 'fade' : kind;
  const ms = t === 'none' ? 0 : Math.max(0, durationMs);
  const frames = FRAMES[t] ?? FRAMES.fade;
  incoming.style.visibility = 'visible';
  if (ms === 0) {
    clear(incoming);
    if (outgoing) outgoing.style.visibility = 'hidden';
    return;
  }
  apply(incoming, frames.inFrom);
  incoming.style.willChange = 'transform, opacity';
  // Force layout so the "from" state is committed before transitioning.
  void incoming.offsetWidth;
  const easing = 'cubic-bezier(0.4, 0, 0.2, 1)';
  incoming.style.transition = `transform ${ms}ms ${easing}, opacity ${ms}ms ${easing}, filter ${ms}ms ${easing}`;
  if (outgoing) {
    outgoing.style.willChange = 'transform, opacity';
    outgoing.style.transition = `transform ${ms}ms ${easing}, opacity ${ms}ms ${easing}, filter ${ms}ms ${easing}`;
  }
  requestAnimationFrame(() => {
    incoming.style.transform = 'none';
    incoming.style.opacity = '1';
    incoming.style.filter = 'none';
    if (outgoing) apply(outgoing, frames.outTo);
  });
  await new Promise((r) => setTimeout(r, ms + 40));
  clear(incoming);
  incoming.style.willChange = '';
  if (outgoing) {
    outgoing.style.visibility = 'hidden';
    outgoing.style.willChange = '';
  }
}

export const DEFAULT_TRANSITION_MS = 800;
